import { FC } from "react"
import { AlertConfigurationModule } from "../types"
import CommonSettings from "./CommonSettings"
import { useAlertSettingData } from "./hooks/useAlertSettingData"
import NoVitalsSettings from "./NoVitalsSettings"
import VitalsSettings from "./VitalsSettings"
import Controls from "@/components/AlertSettingForms/Controls"
import Loader from "@/components/Loader"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import Dialog from "@/components/shared/Dialog"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { IconProps } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { ColorVariant } from "@/types/index"
import { getAlarmDisplayName } from "@/utils/alerts"
import { getErrorMessage } from "@/utils/index"
import { isEmpty } from "@/utils/lodash"
import "./index.scss"

const AlertSettingDetail: FC = () => {
   const {
      id,
      loadingAlertConfiguration,
      errorAlertConfiguration,
      alertConfiguration,
      form,
      isVitalAlert,
      systemAlertType,
      updatingModule,
      loadingAlertConfigurationModule,
      isDeleteConfirmationDialogOpen,
      isDisableConfirmationDialogOpen,
      isAlertEnabled,
      isUpdatingAlertStatus,
      openDialog,
      closeDialog,
      openDisableDialog,
      closeDisableDialog,
      handleSave,
      handleDisable,
      handleEnable,
      handleAlertConfigurationDeletion,
   } = useAlertSettingData()

   const toggleButton = isAlertEnabled
      ? {
           label: "Disable alarm",
           testId: TestId.DISABLE_ALARM_BUTTON,
           iconName: "snoozeAlert" as IconProps["name"],
           outlined: true,
           color: ColorVariant.CERISE,
           onClick: openDisableDialog,
        }
      : {
           label: "Enable alarm",
           testId: TestId.ENABLE_ALARM_BUTTON,
           iconName: "alerts" as IconProps["name"],
           outlined: false,
           color: ColorVariant.HONOLULU_BLUE,
           onClick: handleEnable,
        }

   const {
      name,
      heartRateThreshold,
      respiratoryRateThreshold,
      // oxygenLevel,
      soundClip,
      soundFrequency,
      colorCard,
      colorTypeface,
      wearableType,
      sensorType,
      systemCondition,
      emailMessage,
      // delayPeriod,
   } = form
   const alarmDisplayName = getAlarmDisplayName(name, systemAlertType)
   const staticReadings = systemCondition?.mainThreshold ?? 0
   const wearableReadings = systemCondition?.secondaryThreshold ?? 0

   const renderContent = () => {
      if (loadingAlertConfiguration || isUpdatingAlertStatus) {
         return <Loader />
      }

      if (errorAlertConfiguration) {
         return (
            <span className="alert-setting-detail--error">
               {getErrorMessage(errorAlertConfiguration)}
            </span>
         )
      }

      if (isEmpty(form)) {
         return null
      }

      return (
         <>
            <GridItem size={12}>
               <Grid
                  flexWrap="nowrap"
                  justifyContent="flex-start"
               >
                  <Controls
                     isVitalAlert={isVitalAlert}
                     isAlertEnabled={isAlertEnabled}
                     title={alarmDisplayName}
                     loading={
                        loadingAlertConfigurationModule &&
                        updatingModule === AlertConfigurationModule.NAME
                     }
                     onSave={(newTitle) => {
                        handleSave(
                           { name: newTitle },
                           AlertConfigurationModule.NAME
                        )
                     }}
                  />
               </Grid>
            </GridItem>

            {systemAlertType === AlertSystemConditionType.NO_VITALS &&
               systemCondition && (
                  <NoVitalsSettings
                     disabled={!isAlertEnabled}
                     loading={loadingAlertConfigurationModule}
                     updatingModule={updatingModule}
                     staticReadings={staticReadings}
                     wearableReadings={wearableReadings}
                     systemCondition={systemCondition}
                     handleSave={handleSave}
                  />
               )}

            {isVitalAlert && (
               <VitalsSettings
                  disabled={!isAlertEnabled}
                  loading={loadingAlertConfigurationModule}
                  updatingModule={updatingModule}
                  heartRateThreshold={heartRateThreshold}
                  respiratoryRateThreshold={respiratoryRateThreshold}
                  wearableType={wearableType}
                  sensorType={sensorType}
                  handleSave={handleSave}
               />
            )}

            <CommonSettings
               disabled={!isAlertEnabled}
               loading={loadingAlertConfigurationModule}
               updatingModule={updatingModule}
               alarmName={alarmDisplayName}
               soundClip={soundClip}
               soundFrequency={soundFrequency}
               colorCard={colorCard}
               colorTypeface={colorTypeface}
               emailMessage={emailMessage}
               handleSave={handleSave}
            />

            <GridItem size={12}>
               <Grid
                  className="alert-setting-detail__actions"
                  flexWrap="nowrap"
                  justifyContent="flex-end"
               >
                  <Button
                     className="alert-setting-detail__toggle-button"
                     outlined={toggleButton.outlined}
                     iconName={toggleButton.iconName}
                     iconPosition={ButtonIconPosition.LEFT}
                     color={toggleButton.color}
                     testId={toggleButton.testId}
                     onClick={toggleButton.onClick}
                  >
                     {toggleButton.label}
                  </Button>

                  {isVitalAlert && (
                     <Button
                        className="alert-setting-detail__delete-button"
                        iconName="trashBin"
                        iconPosition={ButtonIconPosition.LEFT}
                        color={ColorVariant.CERISE}
                        testId={TestId.DELETE_ALARM_BUTTON}
                        onClick={() => openDialog()}
                     >
                        Delete alarm
                     </Button>
                  )}
               </Grid>
            </GridItem>
         </>
      )
   }

   return (
      <>
         <Grid
            className="alert-setting-detail"
            direction="column"
            flexWrap="nowrap"
         >
            {renderContent()}
         </Grid>
         {isDeleteConfirmationDialogOpen && (
            <Dialog
               open={isDeleteConfirmationDialogOpen}
               title={`Are you sure you want to permanently delete ${alertConfiguration.name} from the current facility?`}
               titleTestId={TestId.DELETE_ALARM_DIALOG_TITLE}
               buttonTitle="Confirm"
               handleClose={closeDialog}
               handleButtonClick={() =>
                  handleAlertConfigurationDeletion(id || "")
               }
            />
         )}
         {isDisableConfirmationDialogOpen && (
            <Dialog
               open={isDisableConfirmationDialogOpen}
               title={`Are you sure you want to disable ${alarmDisplayName} for the whole facility? The alarm won't trigger until enabled again`}
               buttonTitle="Confirm"
               isDisabled={isUpdatingAlertStatus}
               handleClose={closeDisableDialog}
               handleButtonClick={handleDisable}
            />
         )}
      </>
   )
}

export default AlertSettingDetail
